import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Trophy, Sparkles, Hand, Brain, Crown, Loader2, Lock } from 'lucide-react';
import { gamesApi } from '../lib/api';
import { Button } from '../components/ui/button';

const GAMES = [
    {
        key: 'loteria',
        title: 'Mexican Lotería',
        subtitle: 'Classic 54-card bingo. Pass-and-play with the person you crossed paths with.',
        emoji: '🎴',
        color: 'from-rose-500 via-red-500 to-orange-500',
        to: '/games/loteria',
        available: true,
    },
    {
        key: 'rps',
        title: 'Rock · Paper · Scissors',
        subtitle: 'Best of 5. Fast icebreaker. First to 3 wins.',
        emoji: '✊',
        color: 'from-indigo-500 via-blue-500 to-cyan-500',
        to: '/games/rps',
        available: true,
    },
    {
        key: 'trivia',
        title: 'Where Did We Cross?',
        subtitle: 'Personalized trivia built from your real crossings. 5 questions.',
        emoji: '🧠',
        color: 'from-emerald-500 via-teal-500 to-cyan-500',
        to: '/games/trivia',
        available: true,
    },
    {
        key: 'poker',
        title: 'Poker Night',
        subtitle: 'Coming soon — heads-up Texas hold\'em with your matches.',
        emoji: '🃏',
        color: 'from-slate-600 to-slate-800',
        to: '#',
        available: false,
    },
    {
        key: 'minigolf',
        title: 'Mini Golf',
        subtitle: 'Coming soon — 9 holes, physics-based, tap-to-putt.',
        emoji: '⛳',
        color: 'from-lime-500 to-green-600',
        to: '#',
        available: false,
    },
];

const TIER_LABELS = ['🎯 First Win', '🎮 Rookie', '⭐ Player', '🏆 Champion', '👑 VIP Legend'];

export default function Games() {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(async () => {
        try {
            const res = await gamesApi.getStats();
            setStats(res.data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const total = stats?.total_wins || 0;
    const nextTier = stats?.next_tier;
    const unlocked = stats?.unlocked_tiers || [];
    const progressPct = nextTier
        ? Math.min(100, Math.round((total / nextTier.threshold) * 100))
        : 100;

    return (
        <div className="min-h-screen bg-slate-950 text-white pb-20" data-testid="games-hub">
            <div className="max-w-4xl mx-auto px-4 pt-24">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex items-center gap-3 mb-2">
                        <Sparkles className="w-7 h-7 text-amber-400" />
                        <h1 className="text-3xl sm:text-4xl font-bold" data-testid="games-hub-title">Games</h1>
                    </div>
                    <p className="text-slate-400">
                        Play with someone you've crossed paths with. Win to unlock badges and — at 100 wins — a free VIP messaging boost.
                    </p>
                </div>

                {/* Rewards banner */}
                <div className="rounded-3xl bg-gradient-to-br from-amber-500/20 via-rose-500/10 to-indigo-500/20 border border-white/10 p-5 mb-8" data-testid="games-rewards-banner">
                    {loading ? (
                        <div className="flex items-center gap-2 text-slate-300">
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Loading stats…
                        </div>
                    ) : (
                        <>
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center">
                                        <Trophy className="w-6 h-6 text-amber-300" />
                                    </div>
                                    <div>
                                        <div className="text-xs uppercase tracking-widest text-amber-300">Your wins</div>
                                        <div className="text-3xl font-bold" data-testid="games-total-wins">{total}</div>
                                    </div>
                                </div>
                                {stats?.vip_boost_unlocked && (
                                    <div className="flex items-center gap-1 bg-amber-500/20 border border-amber-400/40 text-amber-200 px-3 py-1 rounded-full text-xs font-semibold" data-testid="games-vip-badge">
                                        <Crown className="w-3 h-3" />
                                        VIP Boost Unlocked
                                    </div>
                                )}
                            </div>

                            {/* Progress to next tier */}
                            {nextTier ? (
                                <div>
                                    <div className="flex justify-between text-xs text-slate-400 mb-1">
                                        <span>Next: {nextTier.icon} {nextTier.label}</span>
                                        <span>{total} / {nextTier.threshold}</span>
                                    </div>
                                    <div className="w-full h-2 bg-white/10 rounded-full overflow-hidden">
                                        <div
                                            className="h-full bg-gradient-to-r from-amber-400 to-rose-500 transition-all duration-500"
                                            style={{ width: `${progressPct}%` }}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="text-xs text-amber-300">Every tier unlocked. Legend status. 👑</div>
                            )}

                            {/* Unlocked tier badges */}
                            <div className="flex flex-wrap gap-2 mt-4">
                                {TIER_LABELS.map((label, idx) => {
                                    const isUnlocked = unlocked.some(t => t.threshold === [1,5,15,50,100][idx]);
                                    return (
                                        <span
                                            key={label}
                                            className={`text-xs px-2.5 py-1 rounded-full border ${
                                                isUnlocked
                                                    ? 'bg-white/10 border-white/20 text-white'
                                                    : 'bg-white/5 border-white/10 text-slate-500'
                                            }`}
                                            data-testid={`games-tier-badge-${idx}`}
                                        >
                                            {isUnlocked ? label : <><Lock className="w-3 h-3 inline mr-1" />{label}</>}
                                        </span>
                                    );
                                })}
                            </div>
                        </>
                    )}
                </div>

                {/* Game cards */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {GAMES.map((g) => (
                        <GameCard key={g.key} g={g} />
                    ))}
                </div>

                {/* Footer */}
                <div className="mt-8 text-center text-xs text-slate-500">
                    <Link to="/games/leaderboard" className="hover:text-white" data-testid="games-leaderboard-link">
                        View leaderboard →
                    </Link>
                </div>
            </div>
        </div>
    );
}

function GameCard({ g }) {
    const inner = (
        <div className={`relative rounded-3xl p-6 h-40 overflow-hidden bg-gradient-to-br ${g.color} ${!g.available ? 'opacity-60' : 'hover:scale-[1.02]'} transition-transform`}>
            <div className="text-5xl mb-2">{g.emoji}</div>
            <div className="font-bold text-lg text-white">{g.title}</div>
            <div className="text-sm text-white/80 mt-1 line-clamp-2">{g.subtitle}</div>
            {!g.available && (
                <span className="absolute top-3 right-3 text-[10px] uppercase tracking-widest bg-black/40 text-white px-2 py-0.5 rounded-full">
                    Soon
                </span>
            )}
        </div>
    );
    if (!g.available) {
        return <div data-testid={`game-card-${g.key}`} aria-disabled>{inner}</div>;
    }
    return (
        <Link to={g.to} data-testid={`game-card-${g.key}`}>
            {inner}
        </Link>
    );
}
